/** Hardcoded component project directory until a dedicated API is available. */
export const COMPONENT_PROJECTS = [
  {
    id: 'safe-t',
    name: 'SAFE-T',
    fullName: 'Secure Aviation Framework for Emerging Technologies',
    subawardees: ['Skyway Range', 'TU - SAFE-T', 'DronePort Network'],
  },
  {
    id: 'aswp',
    name: 'ASWP',
    fullName: 'Advanced Skills for the Workforce Program',
    subawardees: ['Asemio', 'inTulsa - ASWP', 'TU - ASWP', 'Atlas', 'THEC', 'TCC'],
  },
  {
    id: 'cntrl',
    name: 'CNTRL',
    fullName: 'Control Tower Initiative',
    subawardees: ['inTulsa - CNTRL'],
  },
  {
    id: 'g-ace',
    name: 'G-ACE',
    fullName: 'Greenwood AI Community Engagement',
    subawardees: ['Black Tech Street'],
  },
  {
    id: 'arch',
    name: 'ARCH',
    fullName: 'Applied Research Commercialization Hub',
    subawardees: ['TU - ARCH', 'OSU'],
  },
  {
    id: 'tarmac',
    name: 'TARMAC',
    fullName: 'Tulsa Advanced Research and Manufacturing Acceleration Coalition',
    subawardees: ['OMA - TARMAC', 'OSU - TARMAC', 'Tarmac LLC - TARMAC', 'TEDC - TARMAC'],
  },
] as const;

export type ComponentProjectDefinition = (typeof COMPONENT_PROJECTS)[number];

/**
 * Match `component_project_id` from APIs to {@link COMPONENT_PROJECTS}.
 * Comparison is case-insensitive (e.g. API may send `CNTRL` while config id is `cntrl`).
 */
export function findComponentProjectById(
  componentProjectId: string | null | undefined
): ComponentProjectDefinition | undefined {
  if (!componentProjectId) return undefined;
  const needle = componentProjectId.trim().toLowerCase();
  return COMPONENT_PROJECTS.find(
    (project) =>
      project.id.toLowerCase() === needle || project.name.toLowerCase() === needle
  );
}

export function getComponentProjectDisplayName(
  componentProjectId: string | null | undefined
): string {
  const project = findComponentProjectById(componentProjectId);
  if (project) return project.name;
  return componentProjectId?.trim() ?? '';
}

/**
 * Value for outstanding-items `component_project_id` query param (config slug, e.g. `aswp`, `safe-t`).
 */
export function buildComponentProjectIdFilterParam(
  componentProjectId: string | null | undefined
): string | undefined {
  if (!componentProjectId) return undefined;
  const project = findComponentProjectById(componentProjectId);
  return project?.id ?? componentProjectId.trim();
}

export function isKnownComponentProject(id: string | null): id is string {
  return id != null && COMPONENT_PROJECTS.some((project) => project.id === id);
}

/** Display name for a component project id/slug (e.g. `aswp` → `ASWP`). */
export function getComponentProjectGroupScope(
  componentProjectId: string | null | undefined
): string | null {
  const project = findComponentProjectById(componentProjectId);
  return project?.name ?? null;
}

/** Friendly subawardee ids scoped to a component project (config registry). */
export function getConfiguredSubawardeeIdsForComponentProject(
  componentProjectId: string | null | undefined
): string[] {
  const project = findComponentProjectById(componentProjectId);
  if (!project?.subawardees) return [];
  return [...project.subawardees];
}
